import { AdminDashboardRequests } from "../components/dashboard/AdminDashboardRequests";
import { AdminAiUsage } from "../components/dashboard/AdminAiUsage";
import { AdminShell } from "../components/admin/AdminShell";
import { AdminOverview } from "../components/admin/AdminOverview";
import { AdminDomains } from "../components/admin/AdminDomains";
import { AdminPages } from "../components/admin/AdminPages";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Shield, CheckCircle2, XCircle, Clock, Globe, ExternalLink,
  RefreshCw, Lock, Unlock, Eye, AlertTriangle, FileText, Sparkles, ArrowRight, KeyRound, Mail, Phone, LayoutDashboard, Cpu, Layers
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending_approval: { label: "Wartet auf Freigabe", color: "bg-yellow-100 text-yellow-800", icon: Clock },
  provisioning: { label: "Wird eingerichtet", color: "bg-blue-100 text-blue-800", icon: RefreshCw },
  registered: { label: "Registriert", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  active: { label: "Aktiv", color: "bg-green-100 text-green-800", icon: CheckCircle2 },
  rejected: { label: "Abgelehnt", color: "bg-red-100 text-red-800", icon: XCircle },
  registration_failed: { label: "Fehlgeschlagen", color: "bg-red-100 text-red-800", icon: AlertTriangle },
  pending: { label: "Ausstehend", color: "bg-gray-100 text-gray-600", icon: Clock },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span data-testid={`status-badge-${status}`} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cfg.color}`}>
      <Icon className="w-3.5 h-3.5" />
      {cfg.label}
    </span>
  );
}

function DomainCard({ page, adminToken, onRefresh }) {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${adminToken}` };

  const approve = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/approve-domain/${page.id}`, { register_domain: true }, { headers });
      if (res.data.success) {
        toast.success(`Domain ${page.custom_domain} erfolgreich eingerichtet`);
      } else {
        toast.error("Einrichtung fehlgeschlagen: " + JSON.stringify(res.data.provisioning_log));
      }
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler bei Freigabe");
    } finally {
      setLoading(false);
    }
  };

  const reject = async () => {
    if (!reason.trim()) { toast.error("Bitte Begründung eingeben"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/reject-domain/${page.id}`, { reason }, { headers });
      toast.success("Domain abgelehnt");
      setRejecting(false);
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler bei Ablehnung");
    } finally {
      setLoading(false);
    }
  };

  const retry = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/reset-domain/${page.id}`, {}, { headers });
      toast.success("Domain zurückgesetzt - bitte erneut freigeben");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Zurücksetzen");
    } finally {
      setLoading(false);
    }
  };

  const isPending = page.domain_status === "pending_approval";
  const canRetry = page.custom_domain && ["registered", "active", "registration_failed", "rejected"].includes(page.domain_status);

  return (
    <div data-testid={`domain-card-${page.id}`} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900 text-lg">{page.company_name}</h3>
          <p className="text-sm text-gray-500">{page.industry} &middot; {page.city}</p>
        </div>
        <StatusBadge status={page.domain_status} />
      </div>

      <div className="space-y-2 mb-4">
        {page.custom_domain && (
          <div className="flex items-center gap-2 text-sm">
            <Globe className="w-4 h-4 text-gray-400" />
            <span className="font-mono text-gray-700">{page.custom_domain}</span>
          </div>
        )}
        {page.subdomain && (
          <div className="flex items-center gap-2 text-sm">
            <Globe className="w-4 h-4 text-gray-400" />
            <span className="font-mono text-gray-700">{page.subdomain}.webgenerator.io</span>
          </div>
        )}
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <span>Paket: {page.subscription?.package_name || "—"}</span>
          <span>&middot;</span>
          <span>Domain-Typ: {page.domain_type || "—"}</span>
        </div>
        {page.contact_value && (
          <div className="text-sm text-gray-500">Kontakt: {page.contact_value}</div>
        )}
        {page.domain_rejection_reason && (
          <div className="text-sm text-red-600 bg-red-50 p-2 rounded">Grund: {page.domain_rejection_reason}</div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <a href={`${window.location.origin}/page/${page.id}`} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800">
          <Eye className="w-4 h-4" /> Vorschau
        </a>

        {isPending && !rejecting && (
          <>
            <Button data-testid={`approve-btn-${page.id}`} size="sm" onClick={approve} disabled={loading}
              className="bg-green-600 hover:bg-green-700 text-white ml-auto">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              <span className="ml-1.5">Freigeben</span>
            </Button>
            <Button data-testid={`reject-btn-${page.id}`} size="sm" variant="outline" onClick={() => setRejecting(true)}
              className="border-red-300 text-red-600 hover:bg-red-50">
              <XCircle className="w-4 h-4" /><span className="ml-1.5">Ablehnen</span>
            </Button>
          </>
        )}

        {canRetry && !rejecting && (
          <Button data-testid={`retry-btn-${page.id}`} size="sm" variant="outline" onClick={retry} disabled={loading}
            className="border-amber-300 text-amber-700 hover:bg-amber-50 ml-auto">
            <RefreshCw className="w-4 h-4" /><span className="ml-1.5">Zurücksetzen & erneut einrichten</span>
          </Button>
        )}

        {rejecting && (
          <div className="w-full mt-2 space-y-2">
            <Textarea data-testid={`reject-reason-${page.id}`} placeholder="Begründung..." value={reason} onChange={e => setReason(e.target.value)} rows={2} />
            <div className="flex gap-2">
              <Button size="sm" variant="destructive" onClick={reject} disabled={loading}>Ablehnen</Button>
              <Button size="sm" variant="ghost" onClick={() => setRejecting(false)}>Abbrechen</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ContentRequestCard({ req, adminToken, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const headers = { Authorization: `Bearer ${adminToken}` };
  const info = req.page_info || {};

  const generate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/content-requests/${req.id}/generate`, {}, { headers });
      if (res.data.success) {
        toast.success("KI hat neue Version generiert!");
        onRefresh();
      } else {
        toast.error("KI-Generierung fehlgeschlagen: " + (res.data.error || ""));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler bei KI-Generierung");
    } finally { setLoading(false); }
  };

  const approve = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/content-requests/${req.id}/approve`, {}, { headers });
      toast.success("Update live deployed!");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler bei Freigabe");
    } finally { setLoading(false); }
  };

  const reject = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/content-requests/${req.id}/reject`, { reason: "Nicht passend" }, { headers });
      toast.success("Update abgelehnt");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setLoading(false); }
  };

  const statusColors = { open: "bg-yellow-100 text-yellow-800", review: "bg-blue-100 text-blue-800", completed: "bg-green-100 text-green-800", rejected: "bg-red-100 text-red-800" };
  const statusLabels = { open: "Offen", review: "KI-Vorschlag bereit", completed: "Live", rejected: "Abgelehnt" };

  return (
    <div data-testid={`cr-card-${req.id}`} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900">{info.company_name || "—"}</h3>
          <p className="text-sm text-gray-500">{info.industry} · {info.city}</p>
        </div>
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[req.status] || statusColors.open}`}>
          {statusLabels[req.status] || req.status}
        </span>
      </div>

      <div className="bg-slate-50 rounded-lg p-4 mb-4 border border-slate-100">
        <p className="text-sm font-medium text-slate-500 mb-1">Kundenanfrage:</p>
        <p className="text-slate-900">{req.description}</p>
        <p className="text-xs text-slate-400 mt-2">{new Date(req.created_at).toLocaleString("de-DE")}</p>
      </div>

      {/* Preview toggle for review status */}
      {req.status === "review" && req.proposed_content && (
        <div className="mb-4">
          <button onClick={() => setShowPreview(!showPreview)} className="text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1 mb-3">
            <Eye className="w-4 h-4" /> {showPreview ? "Vorschau ausblenden" : "KI-Änderungen anzeigen"}
          </button>
          {showPreview && (
            <div className="border rounded-lg overflow-hidden">
              {/* Show page-level updates (address, phone, etc.) */}
              {req.page_updates && Object.keys(req.page_updates).length > 0 && (
                <div className="bg-amber-50 border-b border-amber-200 p-4">
                  <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">Seitendaten-Änderungen</p>
                  {Object.entries(req.page_updates).map(([key, val]) => {
                    const labels = { address: "Adresse", city: "Stadt", phone: "Telefon", company_name: "Firma", owner_name: "Inhaber", email: "E-Mail", opening_hours: "Öffnungszeiten", services: "Leistungen", prices: "Preise", team: "Team", certifications: "Zertifikate", impressum: "Impressum", datenschutz: "Datenschutz" };
                    return (
                      <div key={key} className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-amber-800">{labels[key] || key}:</span>
                        <span className="text-amber-900">{typeof val === "string" ? val : JSON.stringify(val)}</span>
                      </div>
                    );
                  })}
                </div>
              )}
              <div className="grid md:grid-cols-2 divide-x divide-gray-200">
                <div className="p-4 bg-red-50/30">
                  <p className="text-xs font-bold text-red-600 uppercase tracking-wider mb-3">Aktuell (Live)</p>
                  <ContentDiff content={req.current_content || {}} />
                </div>
                <div className="p-4 bg-green-50/30">
                  <p className="text-xs font-bold text-green-600 uppercase tracking-wider mb-3">Neu (KI-Vorschlag)</p>
                  <ContentDiff content={req.proposed_content} />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <a href={`${window.location.origin}/page/${req.page_id}`} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800">
          <Eye className="w-4 h-4" /> Seite ansehen
        </a>

        {req.status === "open" && (
          <Button data-testid={`generate-btn-${req.id}`} size="sm" onClick={generate} disabled={loading} className="ml-auto bg-indigo-600 hover:bg-indigo-700 text-white">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            KI generieren
          </Button>
        )}

        {req.status === "review" && (
          <>
            <Button data-testid={`approve-cr-${req.id}`} size="sm" onClick={approve} disabled={loading} className="ml-auto bg-green-600 hover:bg-green-700 text-white">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-1.5" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
              Freigeben & Live
            </Button>
            <Button data-testid={`reject-cr-${req.id}`} size="sm" variant="outline" onClick={reject} disabled={loading} className="border-red-300 text-red-600 hover:bg-red-50">
              <XCircle className="w-4 h-4 mr-1" /> Ablehnen
            </Button>
            <Button size="sm" variant="outline" onClick={generate} disabled={loading} className="text-indigo-600 border-indigo-200 hover:bg-indigo-50">
              <RefreshCw className="w-4 h-4 mr-1" /> Neu generieren
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function ContentDiff({ content }) {
  if (!content) return <p className="text-sm text-gray-400">Kein Inhalt</p>;
  const headline = content.headline || content.hero?.headline || "";
  const sub = content.subheadline || content.hero?.subheadline || "";
  const benefits = content.benefits || [];
  const faq = content.faq || [];
  return (
    <div className="space-y-3 text-sm">
      {headline && <div><span className="font-semibold text-gray-700">Headline:</span><p className="text-gray-900">{headline}</p></div>}
      {sub && <div><span className="font-semibold text-gray-700">Subheadline:</span><p className="text-gray-600">{sub}</p></div>}
      {benefits.length > 0 && <div><span className="font-semibold text-gray-700">Benefits:</span>{benefits.map((b, i) => <p key={i} className="text-gray-600">• {typeof b === "string" ? b : `${b.title}: ${b.description}`}</p>)}</div>}
      {faq.length > 0 && <div><span className="font-semibold text-gray-700">FAQ ({faq.length}):</span>{faq.slice(0, 2).map((f, i) => <p key={i} className="text-gray-600 truncate">• {f.question}</p>)}</div>}
      {content.social_links && <div><span className="font-semibold text-green-700">Social Links:</span><p className="text-green-600">{JSON.stringify(content.social_links)}</p></div>}
      {"show_map" in content && <div><span className="font-semibold text-indigo-700">Karte im Kontaktbereich:</span><p className={content.show_map === false ? "text-red-600" : "text-green-600"}>{content.show_map === false ? "Ausgeblendet" : "Angezeigt"}</p></div>}
      {content.page_updates && Object.keys(content.page_updates).length > 0 && (
        <div><span className="font-semibold text-blue-700">Seiten-Updates:</span><p className="text-blue-600">{JSON.stringify(content.page_updates)}</p></div>
      )}
    </div>
  );
}

function PasswordResetCard({ req, adminToken, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const headers = { Authorization: `Bearer ${adminToken}` };

  const approve = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/password-resets/${req.id}/approve`, {}, { headers });
      toast.success("Reset-Link wurde per E-Mail versendet");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler bei Freigabe");
    } finally { setLoading(false); }
  };

  const reject = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/password-resets/${req.id}/reject`, {}, { headers });
      toast.success("Anfrage abgelehnt");
      onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    } finally { setLoading(false); }
  };

  const statusColors = { pending: "bg-yellow-100 text-yellow-800", approved: "bg-green-100 text-green-800", rejected: "bg-red-100 text-red-800" };
  const statusLabels = { pending: "Offen", approved: "Freigegeben", rejected: "Abgelehnt" };
  const isPending = req.status === "pending";

  return (
    <div data-testid={`pr-card-${req.id}`} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-semibold text-gray-900">{req.company_name || "—"}</h3>
          <p className="text-sm text-gray-500 flex items-center gap-1.5 mt-0.5">
            <Mail className="w-3.5 h-3.5" />{req.email}
          </p>
        </div>
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${statusColors[req.status] || statusColors.pending}`}>
          {statusLabels[req.status] || req.status}
        </span>
      </div>

      <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100 space-y-1 text-sm">
        <div className="flex items-center gap-2 text-slate-600">
          <Globe className="w-3.5 h-3.5 text-slate-400" />
          <span className="font-mono">{req.website_url}</span>
        </div>
        <p className="text-xs text-slate-400">{new Date(req.created_at).toLocaleString("de-DE")}</p>
      </div>

      {isPending && (
        <div className="flex items-center gap-2">
          <Button data-testid={`pr-approve-${req.id}`} size="sm" onClick={approve} disabled={loading}
            className="bg-green-600 hover:bg-green-700 text-white ml-auto">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-1.5" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
            Freigeben & Link senden
          </Button>
          <Button data-testid={`pr-reject-${req.id}`} size="sm" variant="outline" onClick={reject} disabled={loading}
            className="border-red-300 text-red-600 hover:bg-red-50">
            <XCircle className="w-4 h-4 mr-1" /> Ablehnen
          </Button>
        </div>
      )}
    </div>
  );
}

export default function AdminPage() {
  const [adminToken, setAdminToken] = useState(localStorage.getItem("admin_token") || "");
  const [loggedIn, setLoggedIn] = useState(false);
  const [tokenInput, setTokenInput] = useState("");
  const [pending, setPending] = useState([]);
  const [allDomains, setAllDomains] = useState([]);
  const [contentRequests, setContentRequests] = useState([]);
  const [passwordResets, setPasswordResets] = useState([]);
  const [tab, setTab] = useState("overview");
  const [tick, setTick] = useState(0);
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${adminToken}` };

  const login = () => {
    if (!tokenInput.trim()) { toast.error("Bitte Admin-Token eingeben"); return; }
    localStorage.setItem("admin_token", tokenInput.trim());
    setAdminToken(tokenInput.trim());
    setLoggedIn(true);
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setAdminToken("");
    setLoggedIn(false);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [pendingRes, allRes, crRes, prRes] = await Promise.all([
        axios.get(`${API}/admin/pending-domains`, { headers }),
        axios.get(`${API}/admin/all-domains`, { headers }),
        axios.get(`${API}/admin/content-requests`, { headers }),
        axios.get(`${API}/admin/password-resets`, { headers }),
      ]);
      setPending(pendingRes.data.pending || []);
      setAllDomains(allRes.data.domains || []);
      setContentRequests(crRes.data.requests || []);
      setPasswordResets(prRes.data.requests || []);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Ungültiger Admin-Token");
        logout();
      } else {
        toast.error("Fehler beim Laden");
      }
    } finally {
      setLoading(false);
    }
  }, [adminToken]);

  useEffect(() => {
    if (adminToken) {
      setLoggedIn(true);
      fetchData();
    }
  }, [adminToken, fetchData]);

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-slate-900 rounded-lg flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Admin-Zugang</h1>
          </div>
          <Input data-testid="admin-token-input" type="password" placeholder="Admin-Token eingeben..." value={tokenInput}
            onChange={e => setTokenInput(e.target.value)} onKeyDown={e => e.key === "Enter" && login()} className="mb-4" />
          <Button data-testid="admin-login-btn" onClick={login} className="w-full bg-slate-900 hover:bg-slate-800">
            <Lock className="w-4 h-4 mr-2" /> Anmelden
          </Button>
        </div>
      </div>
    );
  }

  const openCR = contentRequests.filter(r => r.status === "open" || r.status === "review");
  const pendingPR = passwordResets.filter(r => r.status === "pending");
  const NAV = [
    { id: "overview", label: "Übersicht", icon: LayoutDashboard },
    { id: "domains", label: "Domains & Provisioning", icon: Globe },
    { id: "approvals", label: "Freigaben", icon: CheckCircle2, count: pending.length },
    { id: "content", label: "Content-Updates", icon: FileText, count: openCR.length },
    { id: "passwords", label: "Passwort-Resets", icon: KeyRound, count: pendingPR.length },
    { id: "requests", label: "Termine & SEO", icon: Phone },
    { id: "ai", label: "KI-Nutzung", icon: Cpu },
    { id: "pages", label: "Seiten-Verwaltung", icon: Layers },
  ];
  const Empty = ({ icon: Icon, text, id }) => (
    <div data-testid={id} className="text-center py-20 text-slate-400"><Icon className="w-12 h-12 mx-auto mb-4 opacity-50" /><p className="text-lg font-medium">{text}</p></div>
  );

  return (
    <AdminShell nav={NAV} section={tab} onSelect={setTab} title={NAV.find(n => n.id === tab)?.label} onRefresh={() => { fetchData(); setTick(t => t + 1); }} loading={loading} onLogout={logout}>
      {tab === "overview" && <AdminOverview adminToken={adminToken} tick={tick} />}
      {tab === "domains" && <AdminDomains adminToken={adminToken} tick={tick} />}
      {tab === "pages" && <AdminPages adminToken={adminToken} tick={tick} />}
      {tab === "ai" && <AdminAiUsage adminToken={adminToken} />}
      {tab === "requests" && <AdminDashboardRequests adminToken={adminToken} />}
      {tab === "approvals" && (pending.length === 0 ? <Empty icon={Globe} text="Keine wartenden Freigaben" id="empty-state" /> : (
        <div className="grid gap-4 md:grid-cols-2">{pending.map(page => <DomainCard key={page.id} page={page} adminToken={adminToken} onRefresh={fetchData} />)}</div>
      ))}
      {tab === "content" && (contentRequests.length === 0 ? <Empty icon={FileText} text="Keine Content-Update Requests" id="empty-content" /> : (
        <div className="grid gap-4 md:grid-cols-2">{contentRequests.map(req => <ContentRequestCard key={req.id} req={req} adminToken={adminToken} onRefresh={fetchData} />)}</div>
      ))}
      {tab === "passwords" && (passwordResets.length === 0 ? <Empty icon={KeyRound} text="Keine Passwort-Anfragen" id="empty-passwords" /> : (
        <div className="grid gap-4 md:grid-cols-2">{passwordResets.map(req => <PasswordResetCard key={req.id} req={req} adminToken={adminToken} onRefresh={fetchData} />)}</div>
      ))}
    </AdminShell>
  );
}
